<template>
  <div class="space-y-6">
    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="text-gray-600">Loading entities...</div>
    </div>

    <div v-else class="bg-white border border-gray-200 rounded-lg p-6">
      <h3 class="font-semibold text-gray-900 mb-4 flex items-center">
        <svg class="w-5 h-5 mr-2 text-green-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Entity Browser
      </h3>

      <div class="space-y-2">
        <div v-for="(entityList, type) in entities" :key="type" class="border border-gray-200 rounded-lg">
          <button
            @click="toggleType(type)"
            class="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div class="flex items-center space-x-3">
              <svg v-if="expandedTypes.has(type)" class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <svg v-else class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span class="font-medium text-gray-900 capitalize">
                {{ type.replace('_', ' ') }}
              </span>
              <span class="px-2 py-1 bg-green-100 text-green-700 text-sm rounded-full">
                {{ entityList.length }}
              </span>
            </div>
          </button>

          <div v-if="expandedTypes.has(type)" class="border-t border-gray-200 p-4 bg-gray-50">
            <div class="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
              <div
                v-for="(entity, index) in entityList"
                :key="index"
                class="p-3 bg-white rounded border border-gray-200"
              >
                <div class="text-sm font-medium text-gray-900">
                  {{ entity.value }}
                </div>
                <div v-if="entity.occurrences > 1" class="text-xs text-gray-500 mt-1">
                  Appears {{ entity.occurrences }} times
                </div>
                <div v-if="entity.properties && Object.keys(entity.properties).length > 0" class="mt-2 text-xs text-gray-600">
                  <div v-for="([key, value], idx) in Object.entries(entity.properties).slice(0, 3)" :key="idx">
                    <span class="font-medium">{{ key }}:</span> {{ value }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="relationships.length > 0" class="bg-white border border-gray-200 rounded-lg p-6">
      <h3 class="font-semibold text-gray-900 mb-4">
        Relationships ({{ relationships.length }})
      </h3>
      <div class="space-y-2 max-h-96 overflow-y-auto">
        <div
          v-for="(rel, index) in relationships.slice(0, 50)"
          :key="index"
          class="p-3 bg-gray-50 rounded border border-gray-200 flex items-center space-x-3"
        >
          <span class="font-medium text-gray-900">{{ rel.from }}</span>
          <span class="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
            {{ rel.type }}
          </span>
          <span class="font-medium text-gray-900">{{ rel.to }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EntityBrowser',
  
  data() {
    return {
      entities: {},
      relationships: [],
      expandedTypes: new Set(),
      loading: true
    };
  },
  
  mounted() {
    this.fetchData();
  },
  
  methods: {
    async fetchData() {
      try {
        const [entitiesRes, relationshipsRes] = await Promise.all([
          this.$axios.get('/api/domain-graph/entities'),
          this.$axios.get('/api/domain-graph/relationships')
        ]);
        
        this.entities = entitiesRes.data && typeof entitiesRes.data === 'object' ? entitiesRes.data : {};
        this.relationships = Array.isArray(relationshipsRes.data) ? relationshipsRes.data : [];
      } catch (error) {
        console.error('Error fetching data:', error);
        this.entities = {};
        this.relationships = [];
      } finally {
        this.loading = false;
      }
    },
    
    toggleType(type) {
      if (this.expandedTypes.has(type)) {
        this.expandedTypes.delete(type);
      } else {
        this.expandedTypes.add(type);
      }
      this.$forceUpdate();
    }
  }
};
</script>

<style scoped>
.space-y-6 > * + * {
  margin-top: 1.5rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.space-y-2 > * + * {
  margin-top: 0.5rem;
}

.space-x-3 > * + * {
  margin-left: 0.75rem;
}

.gap-3 {
  gap: 0.75rem;
}
</style>
